import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { createElementColor } from '@src/helpers';
import { usePeersStore } from '@src/stores';

const TEXT_COLOR = '#ffffff';

interface IProps {
  name?: string;
  peerId?: string;
  avatarImgSize?: number;
}

const useAvatarCircle = ({ peerId = '', name, avatarImgSize }: IProps) => {
  // Default viewBox size for SVG when no fixed size is provided
  const viewBoxSize = 100; // This will be our reference size for percentage calculations
  const { peerColor, avatarUrl, peerName } = usePeersStore(
    useShallow((state) => ({
      peerColor: state.getPeerColor(peerId),
      avatarUrl: state.getPeerInfoById(peerId)?.peer_image ?? state.avatarUrl,
      peerName: name || state.getPeerInfoById(peerId)?.peer_name || 'Guest',
    }))
  );

  const { firstWord, lastWord, firstChar, secondChar } = useMemo(() => {
    const splittedName = peerName.split(' ');
    const firstWord = splittedName[0] || '';
    const lastWord = splittedName[splittedName.length > 1 ? splittedName.length - 1 : -1] || '';
    const firstChar = firstWord[0] || '';
    const secondChar = lastWord[0] || firstWord[1] || '';

    return {
      firstWord,
      lastWord,
      firstChar,
      secondChar,
    };
  }, [peerName]);

  const avatarBackgroundColor = useMemo(
    () => (peerColor ? createElementColor(peerColor, 'avatar-background') : '#131D23'),
    [peerColor]
  ); // Default color if no color assigned

  // Use the provided backgroundColor or generate one based on name
  const bgColor = useMemo(() => {
    if (avatarBackgroundColor) return avatarBackgroundColor;

    // Generate color based on name if no background color provided
    const charCodeRed = firstWord.charCodeAt(0);
    const charCodeGreen = lastWord.charCodeAt(0) || charCodeRed;
    const red = Math.pow(charCodeRed, 7) % 200;
    const green = Math.pow(charCodeGreen, 7) % 200;
    const blue = (red + green) % 200;

    return `rgb(${red}, ${green}, ${blue})`;
  }, [avatarBackgroundColor, firstWord, lastWord]);

  const avatarSVG = useCallback(() => {
    const size = avatarImgSize || viewBoxSize;
    // For dynamic sizing, use absolute units (35 is 35% of viewBox)
    const fontSize = avatarImgSize ? Math.round(size * 0.35) : 25;

    return `
          <svg xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          ${avatarImgSize ? `width="${size}px" height="${size}px"` : `width="${viewBoxSize}%" height="${viewBoxSize}%"`}
          viewBox="0 0 ${size} ${size}"
          version="1.1"
          preserveAspectRatio="xMidYMid meet">
              <circle
                  fill="${bgColor}"
                  cx="${size / 2}"
                  cy="${size / 2}"
                  r="${size / 2}"
              />
              <text
                  x="50%"
                  y="50%"
                  style="color:${TEXT_COLOR};
                  line-height:1;
                  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Fira Sans, Droid Sans, Helvetica Neue, sans-serif"
                  alignment-baseline="middle"
                  text-anchor="middle"
                  font-size="${fontSize}"
                  font-weight="bolder"
                  dy=".1em"
                  dominant-baseline="middle"
                  fill="${TEXT_COLOR}">${firstChar.toUpperCase()}${secondChar.toUpperCase()}
              </text>
          </svg>`;
  }, [avatarImgSize, bgColor, firstChar, secondChar]);

  const avatarSVGSrc = useCallback(() => {
    return 'data:image/svg+xml,' + avatarSVG().replace(/#/g, '%23').replace(/"/g, "'").replace(/&/g, '&amp;');
  }, [avatarSVG]);

  const avatarSrc = useMemo(() => {
    if (avatarUrl) return avatarUrl;
    return avatarSVGSrc();
  }, [avatarUrl, avatarSVGSrc]);

  return { avatarSrc };
};

export default useAvatarCircle;
